import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useServices } from "@/app/use-services";
import { ApiError, errorMessage } from "@/lib/api/errors";
import { CONSULT_CATEGORIES, CONSULT_CATEGORY_LABEL, type ConsultAnswer, type ConsultCategory } from "@/lib/api/models/consult";
import { BackLink } from "@/ui/BackLink";
import { Button } from "@/ui/Button";
import { Chip, ErrorNote } from "@/ui/Field";
import { Page } from "@/ui/Page";
import { Body, Display, Label, Mono } from "@/ui/Text";
import { useInvalidatePlan } from "@/features/plan/use-plan";
import { LANGUAGE_QUERY_KEY } from "@/features/you/keys";
import { useQueryClient } from "@tanstack/react-query";

interface Exchange {
  readonly id: string;
  readonly question: string;
  readonly answer: ConsultAnswer;
}

/** The typed way in. Same chart, same reading; this way you can keep the answer. */
export function AskPage() {
  const { api, analytics } = useServices();
  const client = useQueryClient();
  const invalidatePlan = useInvalidatePlan();
  const [category, setCategory] = useState<ConsultCategory>("decision_intelligence");
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState<readonly Exchange[]>([]);
  const navigate = useNavigate();

  const ask = useMutation({
    mutationFn: async (q: string) => {
      const turnRef = crypto.randomUUID();
      const language = client.getQueryData<string | null>(LANGUAGE_QUERY_KEY) ?? undefined;
      const answer = await api.consultTurn({ category, question: q, turnRef, language: language ?? undefined });
      return { id: turnRef, question: q, answer };
    },
    onSuccess: (exchange) => {
      analytics.track({ name: "answer consumed", mode: "typed" });
      setHistory((h) => [exchange, ...h]);
      setQuestion("");
      void invalidatePlan();
    },
    onError: (error) => {
      analytics.track({ name: "ask failed", reason: error instanceof ApiError && error.status === 402 ? "no_time_left" : "error" });
      // Free minutes spent: the paywall page, where subscribing happens.
      if (error instanceof ApiError && error.status === 402) navigate("/subscribe");
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const q = question.trim();
    if (!q || ask.isPending) return;
    // The first question on this page is the start of a typed conversation.
    if (history.length === 0) analytics.track({ name: "conversation started", mode: "typed" });
    ask.mutate(q);
  }

  return (
    <form onSubmit={submit} className="contents">
      <Page
        foot={
          <>
            <textarea
              name="question"
              rows={2}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What’s on your mind?"
              className="w-full resize-none border border-rule bg-transparent px-3 py-2.5 text-[16px] leading-snug outline-none placeholder:text-faint focus:border-ink"
            />
            <Button type="submit" label="Ask" disabled={!question.trim()} busy={ask.isPending} />
          </>
        }
      >
        <BackLink to="/talk" label="Talk" />
        <Label className="mt-3">Ask a question</Label>
        <div role="radiogroup" aria-label="About" className="mt-3 flex flex-wrap gap-2">
          {CONSULT_CATEGORIES.map((c) => (
            <Chip
              key={c}
              label={CONSULT_CATEGORY_LABEL[c]}
              selected={c === category}
              onSelect={() => {
                analytics.track({ name: "question category chosen", category: c });
                setCategory(c);
              }}
            />
          ))}
        </div>
        {history.length === 0 ? (
          <>
            <Display size="md" className="mt-6">Ask about {CONSULT_CATEGORY_LABEL[category].toLowerCase()}.</Display>
            <Body className="mt-3 text-mid">She reads from your chart, the same as in conversation — this way you can keep the answer and come back to it.</Body>
          </>
        ) : (
          <ul className="mt-6 space-y-8">
            {history.map((x) => (
              <li key={x.id}>
                <div className="ml-auto max-w-[82%] bg-ink px-3.5 py-3 text-paper text-[16px]">{x.question}</div>
                <Body className="mt-4 text-[16px]">{x.answer.answer}</Body>
                {x.answer.facts_used.length > 0 && (
                  <div className="mt-4 border-l border-rule pl-3">
                    <Label>What this came from</Label>
                    <ul className="mt-1 space-y-0.5">
                      {x.answer.facts_used.map((f) => <li key={f}><Mono>· {f}</Mono></li>)}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <ErrorNote message={ask.isError && !(ask.error instanceof ApiError && ask.error.status === 402) ? errorMessage(ask.error) : null} />
      </Page>
    </form>
  );
}
